import { useQuery, ApolloError } from '@apollo/client'
import { ALL_CATEGORIES, ALL_FILTER_CATEGORIES, ALL_REGIONS } from '../queries'
import find from 'lodash/find'
import get from 'lodash/get'
import uniqBy from 'lodash/uniqBy'
import { useTranslation } from 'react-i18next'
import first from 'lodash/first'
import {
  IFilterCategory,
  IFilterCategoryItem,
  IFilterItem,
  ICategory,
  IRegion,
} from '../interfaces'
import { FilterInputType } from '../constants/enums'

export interface IUseGetCategories {
  categoryId: string
}

export interface IUseGetCategoriesResult {
  loading: boolean
  error?: ApolloError
  filterCategoryId?: number
  category?: ICategory
  checkboxFilters?: IFilterItem[]
  genderFilters?: IFilterItem[]
  communityFilters?: IFilterItem[]
  regionFilters?: IFilterItem[]
}

const useGetCategories = function ({ categoryId }: IUseGetCategories): IUseGetCategoriesResult {
  const { i18n } = useTranslation()
  const LANG = i18n.language.toLocaleUpperCase() as 'EN' | 'ES'

  const { loading, error, data } = useQuery(ALL_CATEGORIES[LANG])

  const {
    loading: loadingFilteCategories,
    error: errorFilterCategories,
    data: filterCategoriesData,
  } = useQuery(ALL_FILTER_CATEGORIES[LANG])

  const { loading: loadingRegions, error: errorRegions, data: regionsData } = useQuery(ALL_REGIONS)

  if (loading) return { loading, error }

  const allCategories = data[`categories_${LANG}_LA`]['data']

  // Get current category
  const category: ICategory = get(
    find(allCategories, { category: { id: parseInt(categoryId) } }),
    'category',
  )

  // Get all filter categories ids
  const filterCategories: IFilterCategory[] = uniqBy(
    get(category, 'filterCategories', []),
    'filterCategoryId',
  )

  if (loadingFilteCategories) {
    return { loading: loadingFilteCategories, error: errorFilterCategories }
  }

  if (loadingRegions) {
    return { loading: loadingRegions, error: errorRegions }
  }

  const allRegions = regionsData['regions_ALL']['data']

  const allFilterCategoriesData: IFilterCategoryItem[] =
    filterCategoriesData[`filterCategories_${LANG}_LA`]['data']

  const categoryIds = filterCategories.map((cat) => cat.filterCategoryId)

  const currentFilterCategories = allFilterCategoriesData.filter((cat: IFilterCategoryItem) =>
    categoryIds.includes(cat.id),
  )

  const activeFilters = currentFilterCategories.map((cat) => {
    return { ...cat, filters: cat.filters?.filter((filter) => filter.isActive) }
  })

  const regions = allRegions.filter(
    (region: IRegion) =>
      region.isWildcard === false && region.areaId === 1 && region.description !== 'All',
  )

  const categoryItem = first(
    activeFilters.filter((item) => item.doesAllowMultiple && item.isDynamicCategory),
  )

  // Category Id
  const filterCategoryId = categoryItem?.id

  // Get checkbox filters
  const checkboxFilters = categoryItem?.filters.map(
    (filter) => ({ ...filter, type: FilterInputType.CHECKBOX } as IFilterItem),
  )

  // Get gender filters
  const genderFilters = first(
    activeFilters.filter(
      (item) => item.doesAllowMultiple === false && item.isDynamicCategory === false,
    ),
  )?.filters.map(
    (filter) => ({ ...filter, type: FilterInputType.DYNAMIC_MULTI_SELECT } as IFilterItem),
  )

  // Get community filters
  const communityFilters = first(
    activeFilters.filter(
      (item) => item.doesAllowMultiple === true && item.isDynamicCategory === false,
    ),
  )?.filters.map(
    (filter) => ({ ...filter, type: FilterInputType.DYNAMIC_MULTI_SELECT } as IFilterItem),
  )

  // Get region filters
  const regionFilters: IFilterItem[] = regions.map((region: IRegion): IFilterItem => {
    return {
      description: region.description,
      id: region.id,
      type: FilterInputType.NEIGHBORHOOD,
    }
  })

  return {
    loading,
    error,
    filterCategoryId,
    category,
    checkboxFilters,
    genderFilters,
    communityFilters,
    regionFilters,
  }
}

export default useGetCategories
